<script setup lang="ts">
  import { onMounted, onUnmounted } from 'vue'
  import { driver, type Driver } from 'driver.js'
  import 'driver.js/dist/driver.css'
  import '@/assets/driverjs.css'
  import { useTranslation } from '@/composables/useTranslation'
  import { useUnifiedChatStore } from '@/store/app/unifiedChat'

  const OVERLAY_SEEN_KEY = 'merged-chats-release-overlay-seen'
  const NAV_TAB_SELECTOR = '[data-cy="top-nav-ai-assistant"]'
  const FLOATING_BUTTON_SELECTOR = '[data-cy="floating-ai-button"]'
  const ANCHOR_POLL_INTERVAL_MS = 100
  const ANCHOR_POLL_MAX_ATTEMPTS = 30

  const unifiedChat = useUnifiedChatStore()

  const { t, locale } = useTranslation({
    en: {
      step1Title: 'An AI assistant for everything',
      step1Description:
        'General questions, verdicts, and case-related support: now all in one chat.',
      step2Title: 'AI chat is now available everywhere',
      step2Description:
        'Now you can chat with JUPUS AI from every page – just open the inquiry page to get context from it.',
      next: 'Next',
      back: 'Back',
      tryNow: 'Try now'
    },
    de: {
      step1Title: 'Ein KI-Assistent für alles',
      step1Description:
        'Allgemeine Fragen, Urteile und fallbezogene Unterstützung: jetzt alles in einem einzigen Chat.',
      step2Title: 'KI-Chat ist jetzt überall verfügbar',
      step2Description:
        'Mit JUPUS KI können Sie jetzt auf jeder Seite chatten — öffnen Sie einfach die Anfrageseite, um Kontext zu erhalten.',
      next: 'Weiter',
      back: 'Zurück',
      tryNow: 'Jetzt ausprobieren'
    }
  })

  // driver.js's {{current}} / {{total}} placeholders collide with vue-i18n's message
  // compiler, so the progress string is built outside the translation dict.
  const getProgressText = (): string =>
    locale.value === 'de' ? '{{current}} von {{total}}' : '{{current}} of {{total}}'

  let driverObj: Driver | null = null
  let pollHandle: ReturnType<typeof setTimeout> | null = null

  const hasSeenOverlay = (): boolean => {
    try {
      return localStorage.getItem(OVERLAY_SEEN_KEY) === 'true'
    } catch {
      // Treat storage failures as "already seen" so we never re-prompt in a loop.
      return true
    }
  }

  const markOverlayAsSeen = (): void => {
    try {
      localStorage.setItem(OVERLAY_SEEN_KEY, 'true')
    } catch {
      /* localStorage may be blocked — accept the worst case of the overlay re-appearing */
    }
  }

  const anchorsReady = (): boolean => {
    return (
      document.querySelector(NAV_TAB_SELECTOR) !== null &&
      document.querySelector(FLOATING_BUTTON_SELECTOR) !== null
    )
  }

  const startTour = (): void => {
    driverObj = driver({
      showProgress: true,
      progressText: getProgressText(),
      allowClose: true,
      disableActiveInteraction: true,
      // Make the stage cutout sit flush against the anchored element. Default
      // stagePadding (10) leaves a visible halo around the circular floating
      // button in step 2; flush + large radius collapses to a perfect circle
      // for the button (driver caps radius at min(value, w/2, h/2)) and to a
      // tight pill for the nav tab in step 1.
      stagePadding: 0,
      stageRadius: 999,
      nextBtnText: t('next'),
      prevBtnText: t('back'),
      doneBtnText: t('tryNow'),
      steps: [
        {
          element: NAV_TAB_SELECTOR,
          popover: {
            title: t('step1Title'),
            description: t('step1Description'),
            side: 'bottom',
            align: 'start',
            showButtons: ['next'],
            // driver.js renders the previous button into the DOM even when omitted from
            // showButtons, so strip it explicitly on the first step.
            onPopoverRender: (popover) => {
              popover.previousButton?.remove()
            }
          }
        },
        {
          element: FLOATING_BUTTON_SELECTOR,
          popover: {
            title: t('step2Title'),
            description: t('step2Description'),
            side: 'top',
            align: 'end',
            showButtons: ['next', 'previous', 'close']
          }
        }
      ],
      onDestroyed: () => {
        markOverlayAsSeen()
        driverObj = null
      },
      onNextClick: (_element, _step, opts) => {
        if (opts.driver.isLastStep()) {
          // "Try now" — open the side panel; onDestroyed marks the overlay as seen.
          opts.driver.destroy()
          unifiedChat.openSidePanel()
          return
        }
        opts.driver.moveNext()
      }
    })
    driverObj.drive()
  }

  const pollForAnchors = (attempts: number): void => {
    if (hasSeenOverlay()) return
    if (anchorsReady()) {
      startTour()
      return
    }
    if (attempts <= 0) return
    pollHandle = setTimeout(() => pollForAnchors(attempts - 1), ANCHOR_POLL_INTERVAL_MS)
  }

  onMounted(() => {
    if (hasSeenOverlay()) return
    // Both anchors depend on async loads (PostHog flag, user store) — poll briefly
    // rather than racing onMounted against a single nextTick.
    pollForAnchors(ANCHOR_POLL_MAX_ATTEMPTS)
  })

  onUnmounted(() => {
    if (pollHandle !== null) {
      clearTimeout(pollHandle)
      pollHandle = null
    }
    driverObj?.destroy()
    driverObj = null
  })
</script>

<template>
  <div></div>
</template>
